/* eslint-disable @typescript-eslint/no-inferrable-types */
// #region Imports
import { FetchResult } from '@apollo/client';
import { TypedComponent, TypedFunction } from '@trarc/pc-shared';
import { t } from 'i18next';
import React, { createContext, useContext, useEffect, useReducer } from 'react';
import store from 'store2';

import {
  CheckRiskRegistryAnswerInput,
  CreateRiskRegistryAnswersMutation,
  Organization,
  QuestionShorthand,
  QuestionType,
  RemoveRiskRegistryAnswersMutation,
  RiskRegistryAnswerInput,
  Scalars,
  UpdateRiskRegistryAnswerInput,
  useCheckRiskRegistryAnswersLazyQuery,
  useCreateRiskRegistryAnswersMutation,
  useRemoveRiskRegistryAnswersMutation,
  useUpdateRiskHeartbeatRecordsMutation,
  useUpdateRiskRegistryAnswersMutation,
} from '../../types/api.graphql';
import { TranslationKeys } from '../../types/react-i18next';
import { useSessionContext } from '../SessionContext';
import {
  AnswerResponse,
  CheckRiskRegistryData,
  CheckRiskRegistryExactResponse,
  CheckRiskRegistryResponse,
  CheckRiskRegistryResponseAnswers,
  CreateRiskRegistryAnswerResponse,
  GetModalActionButtonsHookReturn,
  HasPendingQuestionsReturn,
  InitializeRiskRegistryQuestionsHook,
  RemoveRiskRegistryAnswerResponse,
  RiskAssessmentPossibleSessionStorageTypes,
  RiskAssessmentQuestionsContextState,
  RiskAssessmentQuestionsDispatch,
  RiskAssessmentQuestionsDispatchContextType,
  RiskAssessmentQuestionsDispatchHook,
  RiskAssessmentQuestionsDispatchTypes,
  RiskAssessmentQuestionsProviderProps,
  RiskAssessmentQuestionsReducerFunction,
  RiskAssessmentQuestionsState,
  RiskAssessmentQuestionsStateContextType,
  RiskAssessmentQuestionsStateHook,
  SendModalAnswersHook,
} from './RiskAssessmentQuestionsContext.types';

// #endregion

// #region Setup
/**
 * Defines the initial state object of the RiskAssessmentQuestions context with all properties set to undefined.
 */
const initialQuestionState: AnswerResponse<any> = {
  answer: undefined,
  answerId: undefined,
  isExpired: true,
};

const initialState: RiskAssessmentQuestionsContextState = {
  isInitialized: false,
  openModalType: 'never',
  activeStep: 0,
  prevActiveStep: 0,
  isOnboarding: true,
  totalQuestions: undefined,
  remainingOrganizationQuestions: undefined,
  hasNoAnswers: false,
  isSilenced: false,
  isResetting: false,
  closedOrgsWithQuestions: undefined,
  organizationId: undefined,
  organizationQueryArguments: undefined,
  PrimaryIdentityManagement_PrimarySolution: initialQuestionState,
  UnusedUserAccounts_ReviewedPastYear: initialQuestionState,
  UnusedUserAccounts_ReviewedDate: initialQuestionState,
  CloudBasedIdentityProtection_Present: initialQuestionState,
  CloudBasedIdentityProtection_Provider: initialQuestionState,
  // mfa questions
  MFAForCloud_ReviewedPastYear: initialQuestionState,
  MFAForCloud_ReviewedDate: initialQuestionState,
  PasswordManagement_ManagementTool: initialQuestionState,
  PasswordManagement_Enforced: initialQuestionState,
  PasswordManagement_Requirements: initialQuestionState,
  PriorSecurityBreach_KnownBreach: initialQuestionState,
  PriorSecurityBreach_DateOfBreach: initialQuestionState,
  InfrastructureProjects_Present: initialQuestionState,
  InfrastructureProjects_DateOfOccurrence: initialQuestionState,
  QualifiedIndividual_Appointed: initialQuestionState,
  SecurityPersonnel_Present: initialQuestionState,
  SecurityPersonnel_Type: initialQuestionState,
  WrittenISP_ReviewedPastYear: initialQuestionState,
  WrittenISP_ReviewedDate: initialQuestionState,
  IncidentResponsePlan_ReviewedPastYear: initialQuestionState,
  IncidentResponsePlan_ReviewedDate: initialQuestionState,
  TechnologySecurityPolicies_Present: initialQuestionState,
  TechnologySecurityPolicies_Reviewed: initialQuestionState,
  TechnologySecurityPolicies_ReviewedDate: initialQuestionState,
  UserSecurityPolicies_Present: initialQuestionState,
  UserSecurityPolicies_Reviewed: initialQuestionState,
  UserSecurityPolicies_ReviewedDate: initialQuestionState,
  VendorRisk_ReviewedPastYear: initialQuestionState,
  VendorRisk_ReviewedDate: initialQuestionState,
  DataStorage_StorageLocation: initialQuestionState,
  CriticalData_StoredOutside: initialQuestionState,
  CriticalData_StorageLocation: initialQuestionState,
  CriticalData_DataType: initialQuestionState,
  BYODPractices_Allowed: initialQuestionState,
  BYODPractices_CriticalInfo: initialQuestionState,
  LocalAdminPermissions_Granted: initialQuestionState,
  LocalAdminPermissions_MFARequired: initialQuestionState,
  CriticalSystemList_Documented: initialQuestionState,
  RemoteAccess_Allow: initialQuestionState,
  RemoteAccess_MFA: initialQuestionState,
  RemoteAccess_CriticalDevices: initialQuestionState,
  PenTestingAndVulnerabilityScans_External: initialQuestionState,
  PenTestingAndVulnerabilityScans_Cadence: initialQuestionState,
  VendorEndpointAccess_Allowed: initialQuestionState,
  VendorEndpointAccess_MFA: initialQuestionState,
  AssetInventory_ReviewedPastYear: initialQuestionState,
  AssetInventory_ReviewedDate: initialQuestionState,
  PatchManagement_Present: initialQuestionState,
  SystemPatching_PatchedRegularly: initialQuestionState,
  VulnerabilityTracking_Present: initialQuestionState,
  EndpointProtection_Present: initialQuestionState,
  EndpointProtection_Logging: initialQuestionState,
  EndpointProtection_WasAllDevices: initialQuestionState,
  EndpointProtection_IsAllDevices: initialQuestionState,
  CorporateEmailDomain_Present: initialQuestionState,
  MFAForEmail_Required: initialQuestionState,
  InboxSecurity_InactiveMailboxRemoved: initialQuestionState,
  InboxSecurity_SharedMailboxReviewed: initialQuestionState,
  EmailFilter_Present: initialQuestionState,
  EmailFilter_Tool: initialQuestionState,
  EmailConfigurations_Enforced: initialQuestionState,
  EmailConfigurations_DKIM: initialQuestionState,
  EmailConfigurations_SPF: initialQuestionState,
  EmailConfigurations_DMARC: initialQuestionState,
  SecurityAwarenessTraining_Present: initialQuestionState,
  SecurityAwarenessTraining_EnrollmentTimeline: initialQuestionState,
  SecurityAwarenessTraining_Cadence: initialQuestionState,
  PhishingSimulation_Present: initialQuestionState,
  PhishingSimulation_EnrollmentTimeline: initialQuestionState,
  PhishingSimulation_Cadence: initialQuestionState,
};

/**
 * Sets up the RiskAssessmentQuestions state context and defaults it to undefined so that if any RiskAssessmentQuestions context hook is used outside of the
 * RiskAssessmentQuestions state context, we can throw an error.
 */
const RiskAssessmentQuestionsStateContext: RiskAssessmentQuestionsStateContextType =
  createContext<RiskAssessmentQuestionsState>(undefined);

/**
 * Sets up the RiskAssessmentQuestions dispatch context and defaults it to undefined so that if any RiskAssessmentQuestions hook that uses the dispatch context is
 * used outside of the RiskAssessmentQuestions dispatch context, we can throw an error.
 */
const RiskAssessmentQuestionsDispatchContext: RiskAssessmentQuestionsDispatchContextType =
  createContext<RiskAssessmentQuestionsDispatch>(undefined);
// #endregion

// #region Reducer
/**
 * Handles a single action dispatch to the RiskAssessmentQuestions context to update the internal state
 * of the context.
 *
 * @param state - {@link RiskAssessmentQuestionsReducerFunction state} description.
 * @param action - {@link RiskAssessmentQuestionsReducerFunction action} description.
 */
export const RiskAssessmentQuestionsReducer: RiskAssessmentQuestionsReducerFunction = (state, action) => {
  const isJson: TypedFunction<[string], boolean> = (input) => {
    try {
      JSON.parse(input);

      return true;
    } catch (exception: unknown) {
      return false;
    }
  };

  switch (action.type) {
    case RiskAssessmentQuestionsDispatchTypes.Initialize:
      // eslint-disable-next-line no-case-declarations
      if (action.payload.length > 0) {
        const [firstOrgAnswers, ...restOrgAnswers] = action.payload;

        const newState: RiskAssessmentQuestionsContextState = firstOrgAnswers.answers
          ? firstOrgAnswers.answers.reduce(
              (accumulator: RiskAssessmentQuestionsContextState, answers: CheckRiskRegistryResponseAnswers | null) => {
                if (answers !== null) {
                  if (answers.answer && !isNaN(new Date(answers.answer).getTime())) {
                    return {
                      ...accumulator,
                      [answers.question.questionShorthand]: {
                        answer: new Date(answers.answer),
                        isExpired:
                          answers.expiresAt !== null &&
                          answers.expiresAt !== undefined &&
                          new Date(answers.expiresAt) <= new Date(),
                        answerId: answers.id,
                      },
                    };
                  } else if (answers.answer && isJson(answers.answer)) {
                    const parsed: unknown = JSON.parse(answers.answer);
                    if (!!parsed && Object.values(parsed).every((value: unknown) => typeof value === 'boolean')) {
                      return {
                        ...accumulator,
                        [answers.question.questionShorthand]: {
                          answer: parsed,
                          isExpired:
                            answers.expiresAt !== null &&
                            answers.expiresAt !== undefined &&
                            new Date(answers.expiresAt) <= new Date(),
                          answerId: answers.id,
                        },
                      };
                    } else {
                      return {
                        ...accumulator,
                        [answers.question.questionShorthand]: {
                          answer: undefined,
                          isExpired:
                            answers.expiresAt !== null &&
                            answers.expiresAt !== undefined &&
                            new Date(answers.expiresAt) <= new Date(),
                          answerId: answers.id,
                        },
                      };
                    }
                  } else {
                    return {
                      ...accumulator,
                      [answers.question.questionShorthand]: {
                        answer: answers.answer,
                        isExpired:
                          answers.expiresAt !== null &&
                          answers.expiresAt !== undefined &&
                          new Date(answers.expiresAt) <= new Date(),
                        answerId: answers.id,
                      },
                    };
                  }
                } else {
                  return accumulator;
                }
              },
              { ...state },
            )
          : { ...state };

        return {
          ...state,
          ...newState,
          isInitialized: true,
          organizationId: firstOrgAnswers.organizationId,
          remainingOrganizationQuestions: restOrgAnswers,
          hasNoAnswers: firstOrgAnswers.hasNoAnswers,
          isSilenced: firstOrgAnswers.isSilenced,
          activeStep: 0,
          prevActiveStep: 0,

          isOnboarding: !firstOrgAnswers.onboardingUntil || new Date(firstOrgAnswers.onboardingUntil) > new Date(),
          openModalType: 'start',
        };
      } else {
        return state;
      }
    case RiskAssessmentQuestionsDispatchTypes.SwitchToNextDealer:
      store.session.set('closedOrgsWithQuestions', [
        ...(state.closedOrgsWithQuestions ? state.closedOrgsWithQuestions : []),
        state.organizationId,
      ]);

      if (state.remainingOrganizationQuestions && state.remainingOrganizationQuestions.length > 0) {
        const [firstOrgAnswers, ...restOrgAnswers] = state.remainingOrganizationQuestions;

        if (firstOrgAnswers.answers) {
          const newState: RiskAssessmentQuestionsContextState = firstOrgAnswers.answers.reduce(
            (accumulator: RiskAssessmentQuestionsContextState, answers: CheckRiskRegistryResponseAnswers | null) => {
              if (answers !== null) {
                if (answers.answer && !isNaN(new Date(answers.answer).getTime())) {
                  return {
                    ...accumulator,
                    [answers.question.questionShorthand]: {
                      answer: new Date(answers.answer),
                      isExpired: answers.expiresAt !== null || answers.expiresAt !== undefined,
                      answerId: answers.id,
                    },
                  };
                } else if (answers.answer && isJson(answers.answer)) {
                  const parsed: unknown = JSON.parse(answers.answer);

                  if (!!parsed && Object.values(parsed).every((value: unknown) => typeof value === 'boolean')) {
                    return {
                      ...accumulator,
                      [answers.question.questionShorthand]: {
                        answer: parsed,
                        isExpired:
                          answers.expiresAt !== null &&
                          answers.expiresAt !== undefined &&
                          new Date(answers.expiresAt) <= new Date(),
                        answerId: answers.id,
                      },
                    };
                  } else {
                    return {
                      ...accumulator,
                      [answers.question.questionShorthand]: {
                        answer: undefined,
                        isExpired:
                          answers.expiresAt !== null &&
                          answers.expiresAt !== undefined &&
                          new Date(answers.expiresAt) <= new Date(),
                        answerId: answers.id,
                      },
                    };
                  }
                } else {
                  return {
                    ...accumulator,
                    [answers.question.questionShorthand]: {
                      answer: answers.answer,
                      isExpired:
                        answers.expiresAt !== null &&
                        answers.expiresAt !== undefined &&
                        new Date(answers.expiresAt) <= new Date(),
                      answerId: answers.id,
                    },
                  };
                }
              } else {
                return accumulator;
              }
            },
            { ...initialState },
          );

          return {
            ...initialState,
            ...newState,
            isInitialized: true,
            organizationId: firstOrgAnswers.organizationId,
            remainingOrganizationQuestions: restOrgAnswers,
            closedOrgsWithQuestions: [
              ...(state.closedOrgsWithQuestions ? state.closedOrgsWithQuestions : []),
              state.organizationId,
            ],
            hasNoAnswers: firstOrgAnswers.hasNoAnswers,
            isSilenced: firstOrgAnswers.isSilenced,
            isOnboarding: firstOrgAnswers.answers.every(
              (answer) =>
                !answer?.organization?.onboardingUntil || new Date(answer?.organization?.onboardingUntil) > new Date(),
            ),
            openModalType: 'start',
          };
        } else {
          return {
            ...initialState,
            isInitialized: true,
            organizationId: firstOrgAnswers.organizationId,
            remainingOrganizationQuestions: restOrgAnswers,
            closedOrgsWithQuestions: [
              ...(state.closedOrgsWithQuestions ? state.closedOrgsWithQuestions : []),
              state.organizationId,
            ],
            hasNoAnswers: firstOrgAnswers.hasNoAnswers,
            isSilenced: firstOrgAnswers.isSilenced,
            isOnboarding: true,
            openModalType: 'start',
          };
        }
      } else {
        return state;
      }
    case RiskAssessmentQuestionsDispatchTypes.UpdateQuestionAnswers:
      // eslint-disable-next-line no-case-declarations
      const updatedAnswersState: RiskAssessmentQuestionsContextState = { ...state };

      Object.keys(action.payload).map((key: string) => {
        updatedAnswersState[key as keyof Pick<RiskAssessmentQuestionsContextState, QuestionShorthand>] = {
          answerId: state[key as keyof Pick<RiskAssessmentQuestionsContextState, QuestionShorthand>]?.answerId,
          answer: action.payload[key],
          isExpired:
            state[key as keyof Pick<RiskAssessmentQuestionsContextState, QuestionShorthand>]?.isExpired ?? false,
        };
      });

      return {
        ...state,
        ...updatedAnswersState,
      };
    case RiskAssessmentQuestionsDispatchTypes.UpdateQuestionAnswerIds:
      // eslint-disable-next-line no-case-declarations
      const updatedAnswerIdState: RiskAssessmentQuestionsContextState = { ...state };

      Object.keys(action.payload).map((key: string) => {
        updatedAnswerIdState[key as keyof Pick<RiskAssessmentQuestionsContextState, QuestionShorthand>] = {
          answerId: action.payload[key as keyof Pick<RiskAssessmentQuestionsContextState, QuestionShorthand>],
          answer: state[key]?.answer,
          isExpired:
            state[key as keyof Pick<RiskAssessmentQuestionsContextState, QuestionShorthand>]?.isExpired ?? false,
        };
      });

      return {
        ...state,
        ...updatedAnswerIdState,
      };
    case RiskAssessmentQuestionsDispatchTypes.IncrementActiveStep:
      store.session.set('activeStep', state.activeStep + 1);
      store.session.set('prevActiveStep', state.activeStep);

      if (state.totalQuestions === state.activeStep + 1) {
        return {
          ...state,
          activeStep: state.activeStep + 1,
          prevActiveStep: state.activeStep,
          openModalType: 'done',
        };
      } else {
        return {
          ...state,
          activeStep: state.activeStep + 1,
          prevActiveStep: state.activeStep,
        };
      }
    case RiskAssessmentQuestionsDispatchTypes.DecrementActiveStep:
      if (state.activeStep - 1 >= 0) {
        store.session.set('activeStep', state.activeStep - 1);
        store.session.set('prevActiveStep', state.activeStep);

        if (state.openModalType === 'done') {
          return {
            ...state,
            activeStep: state.activeStep - 1,
            prevActiveStep: state.activeStep,
            openModalType: 'question',
          };
        } else {
          return {
            ...state,
            activeStep: state.activeStep - 1,
            prevActiveStep: state.activeStep,
          };
        }
      } else {
        return state;
      }
    case RiskAssessmentQuestionsDispatchTypes.SetActiveStep:
      store.session.set('activeStep', action.payload);
      store.session.set('prevActiveStep', action.payload - 1);

      return {
        ...state,
        activeStep: action.payload,
      };
    case RiskAssessmentQuestionsDispatchTypes.SetOpenQuestionModal:
      if (action.payload === 'never') {
        store.session.set('openModalType', action.payload);
      }

      if (state.openModalType === 'close') {
        const getSessionStorageStep = (key: 'activeStep' | 'prevActiveStep'): number | undefined => {
          if (store.session.has(key)) {
            const sessionStorageStep: unknown = store.session.get(key);
            let parsedStepValue: unknown | undefined = undefined;

            if (typeof sessionStorageStep === 'number') {
              parsedStepValue = +sessionStorageStep;
            } else if (typeof sessionStorageStep === 'string' && !isNaN(+sessionStorageStep)) {
              parsedStepValue = +sessionStorageStep;
            }

            return parsedStepValue as number;
          } else {
            return undefined as never;
          }
        };
        const sessionActiveStep: number | undefined = getSessionStorageStep('activeStep');
        const sessionPrevActiveStep: number | undefined = getSessionStorageStep('prevActiveStep');

        if (sessionActiveStep !== undefined && sessionPrevActiveStep !== undefined) {
          return {
            ...state,
            openModalType: action.payload,
            activeStep: sessionActiveStep,
            prevActiveStep: sessionPrevActiveStep,
          };
        }
      }

      if (
        state.activeStep === state.totalQuestions &&
        state.totalQuestions !== undefined &&
        state.activeStep - 1 >= 0
      ) {
        return {
          ...state,
          openModalType: action.payload,
          activeStep: state.activeStep - 1,
        };
      } else {
        return {
          ...state,
          openModalType: action.payload,
        };
      }
    case RiskAssessmentQuestionsDispatchTypes.SetTotalQuestions:
      return {
        ...state,
        totalQuestions: action.payload,
      };
    case RiskAssessmentQuestionsDispatchTypes.SetOrgQueryArguments:
      return {
        ...state,
        organizationQueryArguments: action.payload,
      };
    case RiskAssessmentQuestionsDispatchTypes.ResetQuestionState:
      return {
        ...state,
        activeStep: 0,
        prevActiveStep: 0,
      };
    case RiskAssessmentQuestionsDispatchTypes.ResetSessionAndState:
      store.session.remove('closedOrgsWithQuestions');
      store.session.remove('openModalType');

      return {
        ...initialState,
        isResetting: true,
      };
    case RiskAssessmentQuestionsDispatchTypes.ClearIsResetting:
      return {
        ...state,
        isResetting: false,
      };
    default:
      return state;
  }
};
// #endregion

// #region Provider
/**
 * Provides the RiskAssessmentQuestions state and a dispatch to update the state to all components that
 * are rendered as a child node of this component.
 *
 * @param children - {@link RiskAssessmentQuestionsProviderProps children} description.
 */
export const RiskAssessmentQuestionsProvider: TypedComponent<RiskAssessmentQuestionsProviderProps> = ({
  isNotFromLogin = false,
  children,
}) => {
  const [updateRiskHeartbeatRecords] = useUpdateRiskHeartbeatRecordsMutation();

  const getSessionStorageValue = <Type extends 'number' | 'never' | 'stringArray'>(
    key: keyof Pick<
      RiskAssessmentQuestionsContextState,
      'activeStep' | 'prevActiveStep' | 'openModalType' | 'closedOrgsWithQuestions'
    >,
    expectedType: Type,
  ): RiskAssessmentPossibleSessionStorageTypes[Type] => {
    if (store.session.has(key)) {
      const sessionStorageValue: unknown = store.session.get(key);
      let parsedValue: unknown | undefined = undefined;

      switch (expectedType) {
        case 'number':
          if (sessionStorageValue) {
            if (typeof sessionStorageValue === 'number') {
              parsedValue = +sessionStorageValue;
            } else if (typeof sessionStorageValue === 'string' && !isNaN(+sessionStorageValue)) {
              parsedValue = +sessionStorageValue;
            }
          }
          break;
        case 'never':
          if (!!sessionStorageValue && typeof sessionStorageValue === 'string' && sessionStorageValue === 'never') {
            parsedValue = sessionStorageValue;
          } else {
            parsedValue = undefined;
          }
          break;
        case 'stringArray':
          if (
            !!sessionStorageValue &&
            Array.isArray(sessionStorageValue) &&
            sessionStorageValue.every((value: unknown) => typeof value === 'string')
          ) {
            parsedValue = sessionStorageValue;
          } else {
            parsedValue = undefined;
          }
      }

      return parsedValue as RiskAssessmentPossibleSessionStorageTypes[Type];
    } else {
      return undefined as never;
    }
  };

  const getInitialStateFromSession: TypedFunction<
    [RiskAssessmentQuestionsContextState],
    RiskAssessmentQuestionsContextState
  > = (initialState) => {
    if (isNotFromLogin) {
      return {
        ...initialState,
        openModalType: 'question',
      };
    }

    const newState: Partial<RiskAssessmentQuestionsContextState> = {};

    let activeStep: number | undefined = undefined;
    activeStep = getSessionStorageValue('activeStep', 'number');
    if (activeStep !== undefined) {
      newState['activeStep'] = activeStep;
    }

    let prevActiveStep: number | undefined = undefined;
    prevActiveStep = getSessionStorageValue('prevActiveStep', 'number');
    if (prevActiveStep !== undefined) {
      newState['prevActiveStep'] = prevActiveStep;
    }

    let openModalType: 'never' | undefined = undefined;
    openModalType = getSessionStorageValue('openModalType', 'never');
    if (openModalType !== undefined) {
      newState['openModalType'] = openModalType;
    }

    let closedOrgsWithQuestions: string[] | undefined = undefined;
    closedOrgsWithQuestions = getSessionStorageValue('closedOrgsWithQuestions', 'stringArray');
    if (closedOrgsWithQuestions !== undefined) {
      newState['closedOrgsWithQuestions'] = closedOrgsWithQuestions;
    }

    if (!!newState.activeStep && newState.openModalType !== 'never') {
      newState.openModalType = 'question';
    }

    if (Object.entries(newState).length > 0) {
      return { ...initialState, ...newState };
    } else {
      return initialState;
    }
  };

  const [state, dispatch] = useReducer<RiskAssessmentQuestionsReducerFunction, RiskAssessmentQuestionsContextState>(
    RiskAssessmentQuestionsReducer,
    {
      ...initialState,
      isNotFromLogIn: isNotFromLogin,
    },
    getInitialStateFromSession,
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (state.isInitialized && state.openModalType !== 'never') {
        void updateRiskHeartbeatRecords({
          variables: {
            organizationIds: [
              state.organizationId,
              ...state.remainingOrganizationQuestions.map(
                (remainingOrganizationQuestion: CheckRiskRegistryResponse) =>
                  remainingOrganizationQuestion.organizationId,
              ),
            ],
            sentAt: new Date().toString(),
          },
        });
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [state]);

  return (
    <RiskAssessmentQuestionsStateContext.Provider value={state}>
      <RiskAssessmentQuestionsDispatchContext.Provider value={dispatch}>
        {children}
      </RiskAssessmentQuestionsDispatchContext.Provider>
    </RiskAssessmentQuestionsStateContext.Provider>
  );
};
// #endregion

// #region useRiskAssessmentQuestionsState
/**
 * RiskAssessmentQuestions context hook that retrieves the state from the context, or throws
 * an error if not used in a component that is a child of the RiskAssessmentQuestions context.
 */
export const useRiskAssessmentQuestionsState: RiskAssessmentQuestionsStateHook = () => {
  const context = useContext(RiskAssessmentQuestionsStateContext);

  if (context === undefined) {
    throw new Error('useRiskAssessmentQuestionsState must be used within an RiskAssessmentQuestionsProvider');
  }

  return context;
};
// #endregion

// #region useRiskAssessmentQuestionsDispatch
/**
 * RiskAssessmentQuestions context hook that returns a dispatch function which can dispatch actions to
 * update the state of the RiskAssessmentQuestions context, or throws an error if not used in a
 * component that is a child of the RiskAssessmentQuestions context.
 */
export const useRiskAssessmentQuestionsDispatch: RiskAssessmentQuestionsDispatchHook = () => {
  const context = useContext(RiskAssessmentQuestionsDispatchContext);

  if (context === undefined) {
    throw new Error('useRiskAssessmentQuestionsDispatch must be used within an RiskAssessmentQuestionsProvider');
  }

  return {
    riskAssessmentQuestionsDispatch: context,
  };
};
// #endregion

// #region useGetModalActionButtons
export const useGetModalActionButtons = <Form extends object>(): GetModalActionButtonsHookReturn<Form> => {
  const { activeStep } = useRiskAssessmentQuestionsState();
  const { riskAssessmentQuestionsDispatch } = useRiskAssessmentQuestionsDispatch();

  const getModalActionButtons: GetModalActionButtonsHookReturn<Form>['getModalActionButtons'] = ({
    onNext,
    onPrevious,
    suppressDecrementStep = false,
    onSubmit,
    form,
  }) => {
    return [
      {
        buttonText: t('previous' as TranslationKeys),
        onClick: () => {
          if (onPrevious) {
            onPrevious();
          }
          if (activeStep > 0 && !suppressDecrementStep) {
            riskAssessmentQuestionsDispatch({ type: RiskAssessmentQuestionsDispatchTypes.DecrementActiveStep });
          }
        },
        disabled: activeStep <= 0,
        showButton: activeStep > 0,
      },
      {
        buttonText: t('next' as TranslationKeys),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick: async () => {
          if (onNext) {
            onNext();
          }
          await onSubmit();
        },
        disabled: form.formState.submitCount > 0 && Object.keys(form.formState.errors).length > 0,
        showButton: true,
      },
    ];
  };

  return {
    getModalActionButtons,
  };
};
// #endregion

// #region useSendModalAnswers
export const useSendModalAnswers: SendModalAnswersHook = () => {
  const { organizationId } = useRiskAssessmentQuestionsState();
  const { riskAssessmentQuestionsDispatch } = useRiskAssessmentQuestionsDispatch();
  const [createRiskRegistryAnswers] = useCreateRiskRegistryAnswersMutation();
  const [updateRiskRegistryAnswers] = useUpdateRiskRegistryAnswersMutation();
  const [removeRiskRegistryAnswers] = useRemoveRiskRegistryAnswersMutation();

  const sendModalAnswers: ReturnType<SendModalAnswersHook>['sendModalAnswers'] = (questions) => {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises, no-async-promise-executor
    return new Promise<void>(async (resolve) => {
      const answersToCreate: RiskRegistryAnswerInput | RiskRegistryAnswerInput[] = [];
      const answersToUpdate: UpdateRiskRegistryAnswerInput | UpdateRiskRegistryAnswerInput[] = [];
      const answersToDelete: Scalars['ID'] | Array<Scalars['ID']> = [];

      questions.map((question) => {
        if (question.answer !== undefined && question.answerId === undefined) {
          answersToCreate.push({
            questionShorthand: question.questionShorthand,
            answer: question.answer,
          });
        } else if (question.answer !== undefined && question.answerId !== undefined) {
          answersToUpdate.push({
            answerId: question.answerId.toString(),
            answer: question.answer,
          });
        } else if (question.answer === undefined && question.answerId !== undefined) {
          answersToDelete.push(question.answerId);
        }
      });

      if (answersToCreate.length > 0 && organizationId !== undefined) {
        const result: FetchResult<CreateRiskRegistryAnswersMutation> = await createRiskRegistryAnswers({
          variables: {
            organizationId,
            answers: answersToCreate,
          },
        });

        if (result.data) {
          riskAssessmentQuestionsDispatch({
            type: RiskAssessmentQuestionsDispatchTypes.UpdateQuestionAnswerIds,
            payload: result.data.createRiskRegistryAnswers.reduce(
              (accumulator: Record<string, string>, createdAnswer: CreateRiskRegistryAnswerResponse | null) => {
                if (createdAnswer) {
                  return {
                    ...accumulator,
                    [createdAnswer.question.questionShorthand]: createdAnswer.id,
                  };
                } else {
                  return accumulator;
                }
              },
              {},
            ),
          });
        }
      }

      if (answersToUpdate.length > 0 && organizationId !== undefined) {
        await updateRiskRegistryAnswers({
          variables: {
            organizationId,
            answers: answersToUpdate,
          },
        });
      }

      if (answersToDelete.length > 0) {
        const result: FetchResult<RemoveRiskRegistryAnswersMutation> = await removeRiskRegistryAnswers({
          variables: {
            answerIds: answersToDelete,
          },
        });

        if (result.data) {
          riskAssessmentQuestionsDispatch({
            type: RiskAssessmentQuestionsDispatchTypes.UpdateQuestionAnswerIds,
            payload: result.data.removeRiskRegistryAnswers.reduce(
              (accumulator: Record<string, undefined>, removedAnswer: RemoveRiskRegistryAnswerResponse | null) => {
                if (removedAnswer) {
                  return {
                    ...accumulator,
                    [removedAnswer.question.questionShorthand]: undefined,
                  };
                } else {
                  return accumulator;
                }
              },
              {},
            ),
          });
        }
      }

      resolve();
    });
  };

  return {
    sendModalAnswers,
  };
};
// #endregion

// #region checkPendingQuestions
/**
 * This hook provides the pending-questions state and a function to check for pending or unanswered questions.
 */
export const useHasPendingQuestions = (): HasPendingQuestionsReturn => {
  const [checkRiskRegistryAnswers] = useCheckRiskRegistryAnswersLazyQuery();
  const { activeDealerships } = useSessionContext();

  const checkPendingQuestions: HasPendingQuestionsReturn['checkPendingQuestions'] = async () => {
    return await checkRiskRegistryAnswers({
      variables: {
        organizationInputs: activeDealerships.reduce(
          (accumulator: CheckRiskRegistryAnswerInput[], activeDealership: Organization) => {
            return [
              ...accumulator,
              {
                type:
                  activeDealership.onboardingUntil &&
                  !isNaN(new Date(activeDealership.onboardingUntil).getTime()) &&
                  new Date(activeDealership.onboardingUntil) <= new Date()
                    ? QuestionType.Standard
                    : QuestionType.Onboarding,
                organizationId: activeDealership.id,
              },
            ];
          },
          [],
        ),
      },
      fetchPolicy: 'network-only',
    }).then((result) => {
      const answers: Array<CheckRiskRegistryResponse | null> = result.data.checkRiskRegistryAnswers;
      const updatedAns = answers
        .filter((answerSet: CheckRiskRegistryResponse | null) => answerSet !== null)
        .filter((answerSet: CheckRiskRegistryResponse | null) => !answerSet.hasNoAnswers)
        .map((result: CheckRiskRegistryResponse): CheckRiskRegistryData => {
          const onboardingUntil: string | undefined = activeDealerships.find(
            (activeDealership: Organization) => activeDealership.id === result.organizationId,
          )?.onboardingUntil;

          return {
            ...result,
            onboardingUntil,
          };
        });

      return updatedAns.length > 0;
    });
  };

  return {
    checkPendingQuestions,
  };
};
// #endregion

// #region InitializeRiskRegistryQuestions
/**
 * This hook returns an object with a single named function that is used to initialize the context with all
 * the answers from organizations that still have questions that need to be answered.
 *
 * @returns An object containing the named function to initialize the context.
 */
export const useInitializeRiskRegistryQuestions: InitializeRiskRegistryQuestionsHook = () => {
  const [checkRiskRegistryAnswers] = useCheckRiskRegistryAnswersLazyQuery();
  const { closedOrgsWithQuestions } = useRiskAssessmentQuestionsState();
  const { riskAssessmentQuestionsDispatch } = useRiskAssessmentQuestionsDispatch();
  const { activeDealerships } = useSessionContext();

  /**
   * A function that initializes the context by taking in some number of objects containing the `organizationInputs` objects that have data pertaining
   * to organization IDs and what type of questions we expect back. We then filter out the organizations that aren't relevant and sort them. Some filtering
   * is omitted if `isNotFromLogin` is true, but by default the prop is false.
   *
   * @param answerInput - An object or an array of objects that contains the organization's ID and question type to return which will determine what information is pulled.
   * @param isNotFromLogin - When true, we skip filtering out organizations and use all data that was returned by the API. By default this is off
   * since we want to filter out certain returned data when we initialize the context on log-in. This is primarily used for remediation on the
   * Risk Registry screen.
   */
  const initializeRiskRegistryQuestions: ReturnType<InitializeRiskRegistryQuestionsHook>['initializeRiskRegistryQuestions'] =
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    async (answerInput, isNotFromLogin = false) => {
      const result: CheckRiskRegistryData[] = await checkRiskRegistryAnswers({
        variables: {
          organizationInputs: answerInput,
        },
        fetchPolicy: 'network-only',
      }).then((result: CheckRiskRegistryExactResponse) => {
        if (result.data && result.data.checkRiskRegistryAnswers) {
          const answers: Array<CheckRiskRegistryResponse | null> = result.data.checkRiskRegistryAnswers;
          const updatedResult: Array<CheckRiskRegistryResponse> = answers
            .filter((answerSet: CheckRiskRegistryResponse) =>
              isNotFromLogin
                ? true
                : !closedOrgsWithQuestions?.find((orgId: string) => orgId === answerSet?.organizationId),
            )
            .filter((answerSet: CheckRiskRegistryResponse | null) => answerSet !== null)
            .filter((answerSet: CheckRiskRegistryResponse | null) => (isNotFromLogin ? true : !answerSet.hasNoAnswers))
            .map((result: CheckRiskRegistryResponse): CheckRiskRegistryData => {
              const onboardingUntil: string | undefined = activeDealerships.find(
                (activeDealership: Organization) => activeDealership.id === result.organizationId,
              )?.onboardingUntil;

              return {
                ...result,
                onboardingUntil,
              };
            })
            .sort((answersA: CheckRiskRegistryData | null, answersB: CheckRiskRegistryData | null) => {
              if (answersA && answersB) {
                if (answersA.organizationId < answersB.organizationId) {
                  return -1;
                } else if (answersA.organizationId > answersB.organizationId) {
                  return 1;
                } else {
                  return 0;
                }
              } else {
                return 0;
              }
            });

          return updatedResult;
        } else {
          return [];
        }
      });

      riskAssessmentQuestionsDispatch({
        type: RiskAssessmentQuestionsDispatchTypes.Initialize,
        payload: result,
      });
    };

  return {
    initializeRiskRegistryQuestions,
  };
};
// #endregion
